import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError } from 'rxjs/operators';
import { AppInsightsService } from '../services';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly _appInsights = inject(AppInsightsService);
  private readonly _snackBar = inject(MatSnackBar);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Let MaintenanceInterceptor handle 423 errors
        if (error.status === 423) {
          return throwError(() => error);
        }

        // ERM-4993 Added to allow silent error handling for specific requests instead of popup
        const customHandling = request.context.get(CUSTOM_ERROR_HANDLING);
        if (customHandling === 'silent-mode') {
          return throwError(() => error);
        }
        if (request.url.includes('/auth/admin/user/') && error.status === 409) {
          return throwError(() => error);
        }
        this._appInsights.trackEvent('Error', { error });
        this.trackSpecificError(error, request);
        if (error.status === 0) {
          let message = 'Server is not responding.';

          // ErrorEvent is a client-side error
          if (error.error instanceof ErrorEvent) {
            message = 'Unable to connect to the server.';
          }

          this._snackBar.open(message, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });

          throw new Error(message);
        }
        if (
          error.status === 403 &&
          ['PUT', 'POST', 'DELETE'].includes(request.method)
        ) {
          this._snackBar.open(
            error?.error?.detail ||
              'You are not authorized to perform this action.',
            'Close',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            },
          );

          return throwError(
            () =>
              error?.error?.detail ||
              'You are not authorized to perform this action.',
          );
        } else if (error.status === 401) {
          return throwError(() => 'You do not have the credentials.');
        } else if (error.status === 403) {
          this._snackBar.open(
            error?.error?.detail || 'You are not authorized to view this page.',
            'Close',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            },
          );
          return throwError(() => 'You are not authorized to view this page.');
        } else if (error.status === 404) {
          this._snackBar.open(
            error?.error?.detail || 'Resource not found.',
            'Close',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            },
          );
          return throwError(() => 'Resource not found.');
        }

        // Address add/edit: do not show snackbar; address wrapper shows inline "Your Entry" vs "Suggested" form (no banner).
        // Match any status when `detail.corrected_address` is present (some environments use 400 instead of 422).
        const addressDetail = error.error?.detail;
        if (
          addressDetail &&
          typeof addressDetail === 'object' &&
          addressDetail !== null &&
          'corrected_address' in addressDetail
        ) {
          const d = addressDetail as {
            message?: unknown;
            corrected_address?: unknown;
          };
          const errorPayload = {
            code: error.error?.code,
            message:
              typeof d.message === 'string' ? d.message : error.error?.message,
            corrected_address: d.corrected_address,
          };
          return throwError(() => errorPayload);
        }

        // Escape the snackbar for specialized error messages
        if (
          error.status === 400 &&
          error.error &&
          error.error.module_title &&
          error.error.code === 'required_validation'
        ) {
          const errorMessage = `${error.error.module_title}`;
          return throwError(() => errorMessage);
        }

        if (
          error.status === 400 &&
          error.error &&
          error.error.code === 'missing_sme_qc_marks'
        ) {
          const sections = error.error.sections;
          const docs = error.error.docs;
          const errorMessage = {
            message: 'Please complete the required fields and try again.',
            docs: docs,
            sections: sections,
            code: error.error.code,
          };
          return throwError(() => errorMessage);
        }

        if (
          error.status === 400 &&
          error.error &&
          error.error.code === 'required_validation'
        ) {
          const errorMessage = {
            message: error.error.message,
            section: error.error.section ?? null,
            redirect: error.error.redirect,
            code: error.error.code,
          };
          return throwError(() => errorMessage);
        }

        let errorMessage = 'An unknown error occurred!';

        if (error && error.error instanceof ErrorEvent === false) {
          let message =
            error.error?.detail ||
            error.error?.message ||
            error.message ||
            'Error';
          try {
            // Server-side errors
            if (typeof message === 'string') {
              const newMessage = message.replace(/'/g, '"');
              const errorMsg = newMessage.includes('{')
                ? JSON.parse(newMessage)
                : { message: newMessage };
              const propertiesToUpdate = ['duplicate_file', 'extension'];

              propertiesToUpdate.forEach((property) => {
                if (errorMsg && errorMsg[property]) {
                  message = errorMsg[property];
                }
              });
            }
            if (typeof message === 'object' || !message) {
              message = 'Something went wrong with the server.';
            }
          } catch (e) {
            console.error('error', e);
          }
          errorMessage = `Error: ${message}`;
        }

        if (
          error.url &&
          !DO_NOT_INTERCEPT_LIST.has(errorMessage) &&
          !shouldSkipInterception(request.url, errorMessage) &&
          !DO_NOT_INTERCEPT_REGEX_LIST.some((regex) => regex.test(errorMessage))
        ) {
          this._snackBar.open(errorMessage, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });
        }

        return throwError(() => errorMessage);
      }),
    );
  }

  trackSpecificError(error: HttpErrorResponse, request: HttpRequest<any>) {
    switch (error.status) {
      case 0:
        this._appInsights.trackEvent('ServerConnectionError', {
          message: 'Server not responding',
          method: request.method,
        });
        break;
      case 401:
        this._appInsights.trackError(error, {
          context: 'Unauthorized access',
          url: request.url,
        });
        this._appInsights.trackEvent('AuthenticationError', {
          endpoint: request.url,
          message: 'Unauthorized access',
        });
        break;
      case 403:
        if (error?.error?.detail) {
          this._appInsights.trackEvent('Forbidden Error', {
            endpoint: request.url,
            message: error?.error?.detail,
            method: request.method,
          });
          this._appInsights.trackError(error, {
            context: 'Forbidden Error',
            url: request.url,
          });
        } else {
          this._appInsights.trackEvent('AuthorizationError', {
            endpoint: request.url,
            message: 'Forbidden access',
            method: request.method,
          });
          this._appInsights.trackError(error, {
            context: 'Authorization Error',
            url: request.url,
          });
        }
        break;
      case 404:
        this._appInsights.trackEvent('NotFoundError', {
          endpoint: request.url,
          message: 'Resource not found',
        });
        break;
      case 400:
        this._appInsights.trackEvent('BadRequestError', {
          message: error?.error?.detail || 'Bad request',
          validationErrors: JSON.stringify(error.error),
        });
        break;
      case 500:
        this._appInsights.trackEvent('InternalServerError', {
          message: 'Internal server error',
          method: request.method,
          url: request.url,
        });
        break;
      case 504:
        this._appInsights.trackEvent('GatewayTimeoutError', {
          message: 'Gateway timeout',
          method: request.method,
          url: request.url,
        });
        break;
    }
  }
}

export const DO_NOT_INTERCEPT_LIST = new Set<string>([
  'Error: No Match found. Please try again',
  'Error: You already have access to this Site',
  'Error: You already have access to this Study',
  'Error: No match found. Please try again. If you need help, please contact WCG Client Care.',
  'Error: You have already requested access to this Site. You will receive an email notification once it has been accepted or denied.',
  'Error: You have already requested access to this Study. You will receive an email notification once it has been accepted or denied.',
  'Error: More than one record matches. Please enter a different search criteria and try again. If you need help, please contact WCG Client Care.',
  'Error: Please complete the required fields and try again. If you need help, please contact WCG Client Care.',
  'Error: MAINTENANCE_MODE',
]);

export const DO_NOT_INTERCEPT_REGEX_LIST = [
  /^Error: Legacy ID: \d+ does not exists$/,
];

export const DO_NOT_INTERCEPT_URL_ERROR_LIST = new Map<string, string>([
  [
    '/workspace/questionnaire/',
    'Error: Insufficient permissions to perform action',
  ],
  ['/document/workspace/', 'Error: Insufficient permissions to perform action'],
]);

export function shouldSkipInterception(url: string, error: string): boolean {
  for (const [path, message] of DO_NOT_INTERCEPT_URL_ERROR_LIST.entries()) {
    if (url.includes(path) && error === message) {
      return true;
    }
  }
  return false;
}
